/**
 * Portal Manta · ServicosRodPage
 * Extraído de App.jsx (v29 modular)
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { V, S, fmtNum, sumBy, sigKey, fmtBRL, OBRAS_ROD } from '../../globals.js';
import { CONSOL_FIN } from '../../data/consol-fin.js';
import { SubSidebar } from '../sidebar/SubSidebar.jsx';
import { Breadcrumb } from '../sidebar/Breadcrumb.jsx';
import { SICRO_DESC } from '../../data/sicro-desc.js';
import { THl, TH, TDl, TD } from '../../app-globals.js';

const ServicosRodPage = memo(function ServicosRodPage_Inner({lote}){
  const sig = sigKey(lote);
  const cf = CONSOL_FIN[sig]||{};
  const od = OBRAS_ROD[sig]||{};
  const sistema = od.sistema||[];
  const extLote = sistema.reduce((s,r)=>s+(r.ext||0),0)||1;
  const cores = {'PAVIMENTACAO':V.or,'TERRAPLENOS E CONTENCOES':V.bl,'DRENAGEM':V.gn,
                 'OAES':V.rxn||'#7c3aed','SINALIZACAO':V.am||'#d97706',
                 'ILUMINACAO':V.ind||'#0891b2','SERVICOS PRELIMINARES':V.gr||'#475569'};
  const totSicro = Object.values(cf).reduce((s,arr)=>s+sumBy(arr,'v'),0);
  const servicos = useMemo(()=>{
    return Object.entries(cf).map(([d,items])=>{
      const tot = sumBy(items,'v');
      const totQty = sumBy(items,'q');
      const puMedio = totQty? items.reduce((s,it)=>s+(it.q||0)*(it.p||0),0)/totQty : 0;
      const puCBdiMedio = totQty? items.reduce((s,it)=>s+(it.q||0)*(it.p||0)*(1+(it.b||0)),0)/totQty : 0;
      const porU = {};
      items.forEach(it=>{const u=(it.u||'?').trim();porU[u]=(porU[u]||0)+(it.q||0);});
      const uDom = Object.entries(porU).filter(([u,q])=>q>0).sort((a,b)=>b[1]-a[1])[0];
      return {d,items,tot,totQty,puMedio,puCBdiMedio,uDom,n:items.length};
    }).sort((a,b)=>b.tot-a.tot);
  },[sig]);
  const abc = useMemo(()=>{
    const all = Object.entries(cf).flatMap(([d,items])=>items.map(it=>({...it,_d:d})));
    all.sort((a,b)=>(b.v||0)-(a.v||0));
    let acum=0;
    return all.map((it,i)=>{
      acum += (it.v||0);
      const pctAcum = totSicro?acum/totSicro*100:0;
      const classe = pctAcum<=80?'A':(pctAcum<=95?'B':'C');
      return {...it, _i:i+1, _acum:acum, _pctAcum:pctAcum, _classe:classe};
    });
  },[sig]);
  const abcResumo = {A:abc.filter(x=>x._classe==='A'),B:abc.filter(x=>x._classe==='B'),C:abc.filter(x=>x._classe==='C')};

  // Sub-abas verticais (na SubSidebar lateral)
  const subs = [
    {id:'2.1', k:'serv', n:1, badge:'1', l:'KPIs por Serviço',   sub:'Tabela síntese + matriz Serviço × Rodovia'},
    {id:'2.2', k:'rod',  n:2, badge:'2', l:'KPIs por Rodovia',   sub:'Matriz custo · quantidades'},
    {id:'2.3', k:'tot',  n:3, badge:'3', l:'Totais por Rodovia', sub:'Pav · Dren · Terra · OAE'},
    {id:'2.4', k:'abc',  n:4, badge:'4', l:'Curva ABC',          sub:'Concentração de custo'},
  ];
  const [view,setView] = useState('serv');
  const cur = subs.find(s=>s.k===view) || subs[0];

  return(<div style={{display:'flex',flex:1,overflow:'hidden'}}>
    {/* SubSidebar vertical (esquerda) */}
    <SubSidebar
      groups={[{id:'servrod', label:'Serviços × Rodovias', items:subs}]}
      activeKey={view}
      onSelect={setView}
      storageKey="manta-subbar-servrod"
    />
    {/* Conteúdo (direita) */}
    <div style={{flex:1,display:'flex',flexDirection:'column',minWidth:0,overflow:'hidden'}}>
      {/* Breadcrumb compacto: substitui título + subtítulo + indicador (3 linhas → 1) */}
      <div style={{padding:'7px 14px',background:'#fff',borderBottom:'1px solid '+V.b1,fontSize:10,color:V.gr,display:'flex',gap:8,alignItems:'center',flexShrink:0,flexWrap:'wrap'}}>
        <span style={{fontFamily:'ui-monospace,monospace',fontWeight:800,color:V.bl}}>{cur.id}</span>
        <span style={{color:V.b1}}>›</span>
        <span style={{fontWeight:700,color:V.br}}>{cur.l}</span>
        <span style={{color:V.gr}}>· {cur.sub}</span>
        <span style={{...S.pillBase,background:V.bl,color:'#fff',marginLeft:'auto'}}>OBRAS DE AMPLIAÇÃO</span>
      </div>
      {/* Área rolável de conteúdo */}
      <div style={{flex:1,overflow:'auto',minWidth:0}}>
      <div style={S.page}>
        <div style={S.h1}>Serviços × Rodovias · {lote.nome}</div>
        <div style={S.sub}>
          Extensão: <b>{fmtNum(extLote,1)} km</b> · 
          Total SICRO: <b style={{color:V.br}}>{fmtBRL(totSicro)}</b> · 
          {Object.values(cf).reduce((s,arr)=>s+arr.length,0)} itens em {servicos.length} disciplinas
        </div>

        {/* 2.1 KPIs por Serviço — OBRAS DE AMPLIAÇÃO (reformatado v23: tabela síntese com totais + matriz Serviço × Rodovia) */}
        {view==='serv' && <>
          {/* Cabeçalho de contexto ANTT */}
          <div style={{...S.cardP,marginBottom:14,borderLeft:'4px solid '+V.bl,background:'#eff6ff'}}>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:6,flexWrap:'wrap'}}>
              <span style={{...S.pillBase,background:V.bl,color:'#fff',padding:'3px 10px',fontSize:10}}>ANTT · Agência Reguladora</span>
              <span style={{...S.pillBase,background:V.or,color:'#fff',padding:'3px 10px',fontSize:10}}>OBRAS DE AMPLIAÇÃO</span>
              <span style={{fontSize:11,fontWeight:700,color:V.br}}>Estudos baseados na estrutura ANTT</span>
            </div>
            <div style={{fontSize:10.5,color:V.br,lineHeight:1.5}}>
              Os custos por serviço (disciplina) seguem a estrutura padronizada da <b>Agência Nacional de Transportes Terrestres (ANTT)</b> para concessões rodoviárias federais.
              Disciplinas SICRO consolidadas conforme PER + MEF dos lotes ANTT-PR (L3, L4, L5). Base SICRO: DNIT-PR · BDI ~23%.
              Os indicadores R$/km e P.U. médios servem como benchmark para novos estudos de concessão na mesma estrutura ANTT.
              <br/><b>Escopo:</b> apenas obras de ampliação (Pavimentação, Drenagem, Terraplenos, OAEs, Sinalização, Iluminação, Serviços Preliminares — não inclui manutenção/recuperação/conservação).
            </div>
          </div>

          {/* TABELA 1 — Síntese por Serviço (com linha TOTAL) */}
          <div style={{...S.card,overflow:'hidden',marginBottom:14}}>
            <div style={{padding:'7px 12px',fontSize:10.5,fontWeight:700,color:V.br,background:V.g0,borderBottom:'1px solid '+V.b1,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span>Tabela 1 · Síntese por Serviço — {lote.nome}</span>
              <span style={{fontSize:9.5,fontWeight:500,color:V.gr}}>{servicos.length} disciplinas · {Object.values(cf).reduce((s,arr)=>s+arr.length,0)} itens</span>
            </div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:10.5}}>
              <thead style={S.thRow}><tr style={{borderBottom:'2px solid '+V.b1}}>
                <th style={THl}>Serviço</th><th style={TH}>Itens</th><th style={TH}>Qtd dom.</th><th style={TH}>Und</th>
                <th style={TH}>P.U. s/BDI</th><th style={TH}>P.U. c/BDI</th><th style={TH}>Total</th><th style={TH}>R$/km</th><th style={TH}>%</th>
              </tr></thead>
              <tbody>
                {servicos.map(s=>{
                  const rkm=extLote?s.tot/extLote:0;
                  const pct=totSicro?s.tot/totSicro*100:0;
                  return(<tr key={s.d} style={S.trLine}>
                    <td style={TDl}><span style={{display:'inline-block',width:9,height:9,borderRadius:99,background:cores[s.d]||V.bl,marginRight:7}}/>{s.d}</td>
                    <td style={TD}>{s.n}</td>
                    <td style={TD}>{s.uDom?fmtNum(s.uDom[1],0):'—'}</td>
                    <td style={TD}>{s.uDom?s.uDom[0]:'—'}</td>
                    <td style={TD}>{fmtBRL(s.puMedio,2)}</td>
                    <td style={TD}>{fmtBRL(s.puCBdiMedio,2)}</td>
                    <td style={{...TD,fontWeight:700,color:V.br}}>{fmtBRL(s.tot)}</td>
                    <td style={TD}>{fmtBRL(rkm,0)}</td>
                    <td style={TD}>{pct.toFixed(1)}%</td>
                  </tr>);
                })}
              </tbody>
              <tfoot>
                <tr style={{borderTop:'2px solid '+V.br,background:V.g0}}>
                  <td style={{...TDl,fontWeight:800,color:V.br}}>TOTAL</td>
                  <td style={{...TD,fontWeight:700,color:V.br}}>{servicos.reduce((s,x)=>s+x.n,0)}</td>
                  <td style={TD}>—</td>
                  <td style={TD}>—</td>
                  <td style={TD}>—</td>
                  <td style={TD}>—</td>
                  <td style={{...TD,fontWeight:800,color:V.br}}>{fmtBRL(totSicro)}</td>
                  <td style={{...TD,fontWeight:700,color:V.br}}>{fmtBRL(extLote?totSicro/extLote:0,0)}</td>
                  <td style={{...TD,fontWeight:700,color:V.br}}>100,0%</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* TABELA 2 — Matriz Serviço × Rodovia (NOVA na sub-aba 2.1, reproduzindo o "mesmo quadro por rodovia") */}
          <div style={{...S.card,overflow:'auto',marginBottom:8}}>
            <div style={{padding:'7px 12px',fontSize:10.5,fontWeight:700,color:V.br,background:V.g0,borderBottom:'1px solid '+V.b1,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span>Tabela 2 · Serviço × Rodovia — {lote.nome}</span>
              <span style={{fontSize:9.5,fontWeight:500,color:V.gr}}>{sistema.length} rodovias · valores em R$</span>
            </div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
              <thead style={S.thRow}><tr style={{borderBottom:'2px solid '+V.b1}}>
                <th style={THl}>Disciplina</th>
                {sistema.map(r=>(<th key={r.rod} style={{...TH,minWidth:88}}>{r.rod}<br/><span style={{fontSize:9,color:V.gr,fontWeight:500}}>{fmtNum(r.ext,1)} km · {extLote?(r.ext/extLote*100).toFixed(1):0}%</span></th>))}
                <th style={{...TH,minWidth:90,background:V.g0}}>TOTAL</th>
              </tr></thead>
              <tbody>
                {servicos.map(s=>(<tr key={s.d} style={S.trLine}>
                  <td style={TDl}><span style={{display:'inline-block',width:9,height:9,borderRadius:99,background:cores[s.d]||V.bl,marginRight:7}}/>{s.d}</td>
                  {sistema.map(r=>{const fr=extLote?r.ext/extLote:0;return(<td key={r.rod} style={{...TD,color:V.bl}}>{fmtBRL(s.tot*fr,0)}</td>);})}
                  <td style={{...TD,fontWeight:700,color:V.br,background:V.g0}}>{fmtBRL(s.tot,0)}</td>
                </tr>))}
              </tbody>
              <tfoot>
                <tr style={{borderTop:'2px solid '+V.br,background:V.g0}}>
                  <td style={{...TDl,fontWeight:800,color:V.br}}>TOTAL</td>
                  {sistema.map(r=>{const fr=extLote?r.ext/extLote:0;return(<td key={r.rod} style={{...TD,fontWeight:800,color:V.br}}>{fmtBRL(totSicro*fr,0)}</td>);})}
                  <td style={{...TD,fontWeight:800,color:V.br,background:'#fff'}}>{fmtBRL(totSicro,0)}</td>
                </tr>
                <tr style={{background:V.g0,fontSize:9}}>
                  <td style={{...TDl,color:V.gr,fontStyle:'italic'}}>R$/km da rodovia</td>
                  {sistema.map(r=>{const fr=extLote?r.ext/extLote:0;const tot=totSicro*fr;return(<td key={r.rod} style={{...TD,color:V.gr,fontStyle:'italic'}}>{fmtBRL(r.ext?tot/r.ext:0,0)}</td>);})}
                  <td style={{...TD,color:V.gr,fontStyle:'italic',background:'#fff'}}>{fmtBRL(extLote?totSicro/extLote:0,0)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div style={{fontSize:9.5,color:V.gr,fontStyle:'italic',padding:'4px 4px 12px'}}>
            Nota metodológica: a distribuição R$/rodovia é uma proxy proporcional à extensão de cada rodovia dentro do lote (peso = ext_rodovia / ext_lote).
            Para análise por trecho específico, ver sub-aba 2.3 (Totais por Rodovia) e os anexos técnicos do PER.
          </div>
        </>}

        {/* 2.2 KPIs por rodovia */}
        {view==='rod' && <>
          <div style={{...S.card,overflow:'auto',marginBottom:12}}>
            <div style={{padding:'7px 12px',fontSize:10.5,fontWeight:700,color:V.br,background:V.g0,borderBottom:'1px solid '+V.b1}}>Matriz de custo R$ — Disciplinas × Rodovias</div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
              <thead style={S.thRow}><tr style={{borderBottom:'2px solid '+V.b1}}>
                <th style={THl}>Disciplina</th>
                {sistema.map(r=>(<th key={r.rod} style={{...TH,minWidth:90}}>{r.rod}<br/><span style={{fontSize:9,color:V.gr,fontWeight:500}}>{fmtNum(r.ext,1)} km</span></th>))}
                <th style={TH}>TOTAL</th>
              </tr></thead>
              <tbody>
                {servicos.map(s=>(<tr key={s.d} style={S.trLine}>
                  <td style={TDl}><span style={{display:'inline-block',width:9,height:9,borderRadius:99,background:cores[s.d]||V.bl,marginRight:7}}/>{s.d}</td>
                  {sistema.map(r=>{const fr=extLote?r.ext/extLote:0;return(<td key={r.rod} style={{...TD,color:V.bl,fontWeight:600}}>{fmtBRL(s.tot*fr,0)}</td>);})}
                  <td style={{...TD,fontWeight:700,color:V.br}}>{fmtBRL(s.tot,0)}</td>
                </tr>))}
              </tbody>
            </table>
          </div>
          <div style={{...S.card,overflow:'auto',marginBottom:12}}>
            <div style={{padding:'7px 12px',fontSize:10.5,fontWeight:700,color:V.br,background:V.g0,borderBottom:'1px solid '+V.b1}}>Matriz de quantidades dominantes</div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
              <thead style={S.thRow}><tr style={{borderBottom:'2px solid '+V.b1}}>
                <th style={THl}>Disciplina</th><th style={TH}>Und</th>
                {sistema.map(r=>(<th key={r.rod} style={{...TH,minWidth:80}}>{r.rod}</th>))}
                <th style={TH}>TOTAL</th>
              </tr></thead>
              <tbody>
                {servicos.map(s=>(<tr key={s.d} style={S.trLine}>
                  <td style={TDl}><span style={{display:'inline-block',width:9,height:9,borderRadius:99,background:cores[s.d]||V.bl,marginRight:7}}/>{s.d}</td>
                  <td style={{...TD,color:V.gr,fontSize:9.5}}>{s.uDom?s.uDom[0]:'—'}</td>
                  {sistema.map(r=>{const fr=extLote?r.ext/extLote:0;const q=s.uDom?s.uDom[1]*fr:0;return(<td key={r.rod} style={TD}>{q?fmtNum(q,0):'—'}</td>);})}
                  <td style={{...TD,fontWeight:700}}>{s.uDom?fmtNum(s.uDom[1],0):'—'}</td>
                </tr>))}
              </tbody>
            </table>
          </div>
        </>}

        {/* 2.3 Totais por rodovia */}
        {view==='tot' && <>
          <div style={{...S.card,overflow:'auto',marginBottom:12}}>
            <div style={{padding:'7px 12px',fontSize:10.5,fontWeight:700,color:V.br,background:V.g0,borderBottom:'1px solid '+V.b1}}>Totais de serviços por rodovia · KPIs (estrutura de pavimento)</div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
              <thead style={S.thRow}><tr style={{borderBottom:'2px solid '+V.b1}}>
                <th style={THl}>Rodovia</th><th style={TH}>Ext (km)</th><th style={TH}>%</th>
                <th style={TH}>Pavim. (m²)</th><th style={TH}>Drenagem</th><th style={TH}>Terra. (m³)</th><th style={TH}>OAE</th><th style={TH}>Sinalização</th>
                <th style={TH}>R$ total</th><th style={TH}>R$/km</th>
              </tr></thead>
              <tbody>
                {sistema.map(r=>{
                  const fr = extLote?r.ext/extLote:0;
                  const q = (d)=>sumBy(cf[d]||[],'q')*fr;
                  const tot = totSicro*fr;
                  return(<tr key={r.rod} style={S.trLine}>
                    <td style={{...TDl,fontWeight:700,color:V.br}}>{r.rod}</td>
                    <td style={TD}>{fmtNum(r.ext,1)}</td>
                    <td style={TD}>{(fr*100).toFixed(1)}%</td>
                    <td style={TD}>{fmtNum(q('PAVIMENTACAO'),0)}</td>
                    <td style={TD}>{fmtNum(q('DRENAGEM'),0)}</td>
                    <td style={TD}>{fmtNum(q('TERRAPLENOS E CONTENCOES'),0)}</td>
                    <td style={TD}>{fmtNum(q('OAES'),0)}</td>
                    <td style={TD}>{fmtNum(q('SINALIZACAO'),0)}</td>
                    <td style={{...TD,fontWeight:700,color:V.br}}>{fmtBRL(tot,0)}</td>
                    <td style={TD}>{fmtBRL(r.ext?tot/r.ext:0,0)}</td>
                  </tr>);
                })}
              </tbody>
            </table>
          </div>
        </>}

        {/* 2.4 Curva ABC */}
        {view==='abc' && <>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:12}}>
            {['A','B','C'].map(cls=>{
              const arr = abcResumo[cls];
              const tot = arr.reduce((s,x)=>s+(x.v||0),0);
              const cor = cls==='A'?V.or:(cls==='B'?V.am||'#d97706':V.gn);
              return(<div key={cls} style={{...S.cardP,borderLeft:'4px solid '+cor}}>
                <div style={{fontSize:11,fontWeight:700,color:cor}}>CLASSE {cls}</div>
                <div style={{fontSize:14,fontWeight:700,color:V.br,marginTop:2}}>{arr.length} itens</div>
                <div style={{fontSize:10,color:V.gr,marginTop:2}}>{fmtBRL(tot,0)} · {totSicro?(tot/totSicro*100).toFixed(1):0}% do total</div>
                <div style={{fontSize:9.5,color:V.gr,marginTop:4}}>{cls==='A'?'Cumulativo até 80%':(cls==='B'?'80% a 95%':'95% a 100%')}</div>
              </div>);
            })}
          </div>
          <div style={{...S.card,overflow:'hidden'}}>
            <div style={{padding:'7px 12px',fontSize:10.5,fontWeight:700,color:V.br,background:V.g0,borderBottom:'1px solid '+V.b1}}>Curva ABC — Top 50 itens</div>
            <div style={{maxHeight:480,overflow:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
                <thead style={S.thRow}><tr style={{borderBottom:'2px solid '+V.b1}}>
                  <th style={{...THl,width:36}}>#</th><th style={{...THl,width:36}}>Cl</th>
                  <th style={THl}>SICRO</th><th style={THl}>Descrição</th><th style={THl}>Disc.</th>
                  <th style={TH}>Und</th><th style={TH}>Qtd</th><th style={TH}>P.U. c/BDI</th><th style={TH}>R$ Total</th><th style={TH}>%</th><th style={TH}>% acum</th>
                </tr></thead>
                <tbody>
                  {abc.slice(0,50).map((r,i)=>{
                    const cor = r._classe==='A'?V.or:(r._classe==='B'?V.am||'#d97706':V.gn);
                    const pct = totSicro?(r.v/totSicro*100):0;
                    return(<tr key={i} style={S.trLine}>
                      <td style={TD}>{r._i}</td>
                      <td style={{...TD,fontWeight:700,color:cor}}>{r._classe}</td>
                      <td style={{...TDl,fontFamily:'monospace',fontSize:9.5}}>{r.c}</td>
                      <td style={{...TDl,maxWidth:280,whiteSpace:'normal'}}>{SICRO_DESC[r.c]||''}</td>
                      <td style={{...TDl,fontSize:9.5,color:V.gr}}>{r._d}</td>
                      <td style={TD}>{r.u}</td>
                      <td style={TD}>{fmtNum(r.q,0)}</td>
                      <td style={TD}>{fmtNum(r.p*(1+r.b),2)}</td>
                      <td style={{...TD,fontWeight:700,color:V.br}}>{fmtBRL(r.v,0)}</td>
                      <td style={TD}>{pct.toFixed(2)}%</td>
                      <td style={TD}>{r._pctAcum.toFixed(1)}%</td>
                    </tr>);
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>}
      </div>
    </div>
    </div>
  </div>);
});

export { ServicosRodPage };
export default ServicosRodPage;
